import { type IrrigationEventProto } from "verdi-web-client/Models/Scheduling/IrrigationEventProto";

import {
    SproutAutomationMode,
    type SproutAutomationSettings,
} from "verdi-common/schemas/device/subSchemas/sproutAutomationSettings";
import type MapEntityBase from "../map-management/map-entities/map-entity-base";
import { getAutomationInfo, getAutomationMode, isAutomationEnabled } from "./attributes/automation/getters";
import { getOperatingMode, isDeviceInManualMode } from "./attributes/operating-mode/getters";
import { getBatteryLevel } from "./attributes/battery/getters";
import { getDisplayName, isSensorOnly } from "./attributes/general";
import { IRRIGATION_STATE } from "./attributes/irrigation/definitions";
import { getIrrigationDetails, getIrrigationState } from "./attributes/irrigation/getters";
import {
    getLastContactDate,
    getLastNetworkTestPeriod,
    getSignalLevel,
    getUplinkPeriod,
} from "./attributes/network/getters";
import { OPERATING_MODE } from "./attributes/operating-mode/definitions";

export interface DeviceAttributes {
    general: {
        friendlyName: string;
        isSensorOnly: boolean;
    };
    automation: {
        isAutomationEnabled: boolean;
        automationMode: SproutAutomationMode;
        automationInfo: SproutAutomationSettings | null;
    };
    battery: {
        batteryLevel: number;
    };
    network: {
        signalLevel: number;
        lastContactDate: Date | null;
        lastNetworkTestPeriod: {
            currentlyConductingNetworkTest: boolean;
            lastNetworkTestStart: Date | null;
            lastNetworkTestEnd: Date | null;
        };
        uplinkPeriod: {
            uplinkPeriod: number | null;
        };
    };
    operatingMode: {
        isManualMode: boolean;
        isManualModeTransitioning: boolean;
        targetOperatingMode: OPERATING_MODE | null;
        operatingMode: OPERATING_MODE | null;
    };
    irrigation: {
        irrigationState: IRRIGATION_STATE;
        hasOpenValve: boolean | null;
        percentageScheduledIrrigationCompleted: number | undefined;
        irrigationEndTime: Date | undefined;
        shouldBeIrrigating: boolean;
        closestEvent: IrrigationEventProto | undefined;
        zoneIrrigationStateUncertain: boolean;
    };
}

/**
 * Logic for commonly referred to device data
 */
export function composeDeviceAttributes({ model }: { model: MapEntityBase["model"] }): DeviceAttributes {
    const {
        hasOpenValve,
        percentageScheduledIrrigationCompleted,
        irrigationEndTime,
        shouldBeIrrigating,
        closestEvent,
        zoneIrrigationStateUncertain,
    } = getIrrigationDetails({ model });
    const { isManualModeTransitioning, targetOperatingMode, operatingMode } = getOperatingMode({ model });

    return {
        general: {
            friendlyName: getDisplayName({ model }),
            isSensorOnly: isSensorOnly({ model }),
        },
        automation: {
            isAutomationEnabled: isAutomationEnabled({ model }),
            automationMode: getAutomationMode({ model }),
            automationInfo: getAutomationInfo({ model }),
        },
        battery: {
            batteryLevel: getBatteryLevel({ model }),
        },
        network: {
            signalLevel: getSignalLevel({ model }),
            lastContactDate: getLastContactDate({ model }),
            lastNetworkTestPeriod: getLastNetworkTestPeriod({ model }),
            uplinkPeriod: getUplinkPeriod({ model }),
        },
        operatingMode: {
            isManualMode: isDeviceInManualMode({ model }),
            isManualModeTransitioning,
            targetOperatingMode,
            operatingMode,
        },
        irrigation: {
            irrigationState: getIrrigationState({ model }),
            hasOpenValve,
            percentageScheduledIrrigationCompleted,
            irrigationEndTime,
            shouldBeIrrigating,
            closestEvent,
            zoneIrrigationStateUncertain,
        },
    };
}
